import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CopyIcons from '@/icons/CopyIcons'
import StarIcons from '@/icons/StarIcons'
import {
    createFeedbackToken,
    getFeedbackTokensByTicketID,
    getFeedbacksByTicketID,
    revokeFeedbackToken,
} from '@/modules/Ticket/services'
import {
    FeedbackStats,
    FeedbackToken,
    TicketFeedback as TicketFeedbackType,
} from '@/modules/Ticket/types/response'
import { formatFromUTC } from '@/utils/function'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Chip, Typogaphy } from 'vnetwork-ui'
import CreateFeedbackTokenModal from './CreateFeedbackTokenModal'

interface TicketFeedbackProps {
    ticket_id: number
    ticket_status: string
}

const TicketFeedback: React.FC<TicketFeedbackProps> = ({
    ticket_id,
    ticket_status,
}) => {
    const { t } = useTranslation()
    const { t: tTicket } = useTranslation('ticket')
    const { setConfirmation } = useConfirmation()
    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [copiedToken, setCopiedToken] = useState<string | null>(null)

    // Only show for completed/closed tickets
    const canCreateToken = ['completed', 'closed'].includes(ticket_status)

    // Fetch feedback tokens
    const { data: tokensData, renderState: tokensRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.TICKET_FEEDBACK_TOKENS, String(ticket_id)],
            queryFn: () => getFeedbackTokensByTicketID(ticket_id),
            enabled: canCreateToken,
        })

    // Fetch feedbacks
    const { data: feedbacksData, renderState: feedbacksRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.TICKET_FEEDBACKS, String(ticket_id)],
            queryFn: () => getFeedbacksByTicketID(ticket_id),
            enabled: canCreateToken,
        })

    // Create token mutation
    const { mutateAsync: createTokenMutation, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createFeedbackToken },
            {
                successMessage: t('feedback-token-created'),
                reactQueryKeys: [QUERY_KEYS.TICKET_FEEDBACK_TOKENS],
            }
        )

    // Revoke token mutation
    const { mutateAsync: revokeTokenMutation, isPending: isRevoking } =
        useMutationWithHandler(
            { mutationFn: revokeFeedbackToken },
            {
                successMessage: t('feedback-token-revoked'),
                reactQueryKeys: [QUERY_KEYS.TICKET_FEEDBACK_TOKENS],
            }
        )

    const tokens: FeedbackToken[] = tokensData?.results || []
    const feedbacks: TicketFeedbackType[] = feedbacksData?.feedbacks || []
    const stats: FeedbackStats | null = feedbacksData?.stats || null

    const handleCopyLink = (token: string) => {
        const feedbackUrl = `${window.location.origin}/feedback/${token}`
        navigator.clipboard.writeText(feedbackUrl)
        setCopiedToken(token)
        setTimeout(() => setCopiedToken(null), 2000)
    }

    const handleRevokeToken = (tokenId: number) => {
        setConfirmation({
            open: true,
            title: t('feedback-revoke-confirm-title'),
            description: t('feedback-revoke-confirm-description'),
            onConfirm: async () => {
                await revokeTokenMutation(tokenId)
            },
        })
    }

    const getTokenStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'success'
            case 'used':
                return 'primary'
            case 'revoked':
                return 'error'
            case 'expired':
                return 'warning'
            default:
                return 'default'
        }
    }

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, i) => (
            <StarIcons
                key={i}
                className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
            />
        ))
    }

    if (!canCreateToken) {
        return null
    }

    return (
        <div className="space-y-4 p-4">
            {/* Feedback Stats Summary */}
            {stats && stats.total_feedbacks > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                            {renderStars(Math.round(stats.average_rating))}
                        </div>
                        <span className="text-lg font-semibold text-blue-700">
                            {stats.average_rating.toFixed(1)} / 5.0
                        </span>
                        <span className="text-sm text-gray-500">
                            ({stats.total_feedbacks}{' '}
                            {t('feedback-total-reviews')})
                        </span>
                    </div>
                </div>
            )}

            {/* Feedback Tokens Section */}
            <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center justify-between mb-4">
                    <Typogaphy
                        size="medium"
                        className="font-semibold text-gray-800"
                    >
                        {t('feedback-tokens')}
                    </Typogaphy>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setOpenCreateModal(true)}
                        disabled={isCreating}
                    >
                        {t('feedback-create-token')}
                    </Button>
                </div>

                {tokensRenderState()}

                {tokens.length === 0 ? (
                    <Alert variant="info">{t('feedback-no-tokens')}</Alert>
                ) : (
                    <div className="space-y-3">
                        {tokens.map((token) => (
                            <div
                                key={token.token_id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Chip
                                            label={t(
                                                `feedback-status-${token.status}`
                                            )}
                                            size="small"
                                            color={getTokenStatusColor(
                                                token.status
                                            )}
                                        />
                                        {token.recipient_name && (
                                            <span className="text-sm text-gray-600">
                                                {token.recipient_name}
                                            </span>
                                        )}
                                        {token.recipient_email && (
                                            <span className="text-sm text-gray-500">
                                                ({token.recipient_email})
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {t('feedback-created-by')}:{' '}
                                        {token.employee_name || 'N/A'} |{' '}
                                        {t('feedback-expires-at')}:{' '}
                                        {formatFromUTC(token.expires_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {token.status === 'active' && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() =>
                                                    handleCopyLink(token.token)
                                                }
                                                startIcon={
                                                    <CopyIcons className="w-4 h-4" />
                                                }
                                            >
                                                {copiedToken === token.token
                                                    ? t('copied')
                                                    : t('copy-link')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    handleRevokeToken(
                                                        token.token_id
                                                    )
                                                }
                                                disabled={isRevoking}
                                            >
                                                {tTicket('revoke')}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Feedbacks List Section */}
            {feedbacks.length > 0 && (
                <div className="bg-white rounded-lg border p-4">
                    <Typogaphy
                        size="medium"
                        className="font-semibold text-gray-800 mb-4"
                    >
                        {t('feedback-list')} ({feedbacks.length})
                    </Typogaphy>

                    {feedbacksRenderState()}

                    <div className="space-y-3">
                        {feedbacks.map((feedback) => (
                            <div
                                key={feedback.feedback_id}
                                className="p-3 bg-gray-50 rounded-lg border"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        {renderStars(feedback.rating)}
                                        <span className="font-medium">
                                            {feedback.rating}/5
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        {formatFromUTC(feedback.created_at)}
                                    </span>
                                </div>
                                {/* Điểm từng tiêu chí. `rating` ở trên chỉ là
                                    trung bình cộng của hai cột này. */}
                                {(feedback.rating_support_quality != null ||
                                    feedback.rating_staff_attitude != null) && (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-xs text-gray-600">
                                        {feedback.rating_support_quality !=
                                            null && (
                                            <span className="flex items-center gap-1">
                                                {t('feedback-quality-rating')}:
                                                {renderStars(
                                                    feedback.rating_support_quality
                                                )}
                                                <span className="font-medium">
                                                    {
                                                        feedback.rating_support_quality
                                                    }
                                                    /5
                                                </span>
                                            </span>
                                        )}
                                        {feedback.rating_staff_attitude !=
                                            null && (
                                            <span className="flex items-center gap-1">
                                                {t('feedback-attitude-rating')}:
                                                {renderStars(
                                                    feedback.rating_staff_attitude
                                                )}
                                                <span className="font-medium">
                                                    {
                                                        feedback.rating_staff_attitude
                                                    }
                                                    /5
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                )}
                                {feedback.feedback_text && (
                                    <p className="text-sm text-gray-700 mb-2">
                                        {feedback.feedback_text}
                                    </p>
                                )}
                                {feedback.feedback_by_name && (
                                    <div className="text-xs text-gray-500">
                                        - {feedback.feedback_by_name}
                                        {feedback.feedback_by_email &&
                                            ` (${feedback.feedback_by_email})`}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Create Token Modal */}
            <CreateFeedbackTokenModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                onSubmit={async (data) => {
                    await createTokenMutation({
                        ticket_id,
                        ...data,
                    })
                }}
                isLoading={isCreating}
            />
        </div>
    )
}

export default TicketFeedback
